import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save } from 'lucide-react';
import { useAuth } from '@/core/auth/AuthContext';
import { useWarehouses } from '../hooks/useWarehouses';
import { Input, Select } from '@/components/ui';
import type { Warehouse, WarehouseCreatePayload } from '../types/warehouses.types';

interface WarehouseModalProps {
  isOpen: boolean;
  onClose: () => void;
  warehouse?: Warehouse;
}

export function WarehouseModal({ isOpen, onClose, warehouse }: WarehouseModalProps) {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const { createWarehouse, updateWarehouse, isCreating, isUpdating } = useWarehouses();
  const isSaving = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WarehouseCreatePayload>({
    defaultValues: {
      company_id: companyId,
      name: '',
      code: '',
      location: '',
      is_active: true,
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (warehouse) {
        reset({
          company_id: warehouse.company_id,
          name: warehouse.name,
          code: warehouse.code || '',
          location: warehouse.location || '',
          is_active: warehouse.is_active,
        });
      } else {
        reset({
          company_id: companyId,
          name: '',
          code: '',
          location: '',
          is_active: true,
        });
      }
    }
  }, [isOpen, warehouse, reset, companyId]);

  if (!isOpen) return null;

  const onSubmit = async (data: WarehouseCreatePayload) => {
    if (warehouse) {
      await updateWarehouse({ id: warehouse.id, payload: data });
    } else {
      await createWarehouse(data);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {warehouse ? 'Chỉnh sửa kho bãi' : 'Thêm mới kho bãi'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <form id="warehouse-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Tên kho bãi <span className="text-rose-500">*</span>
              </label>
              <Input
                {...register('name', { required: 'Vui lòng nhập tên kho' })}
                placeholder="Ví dụ: Kho chính, Kho tạm..."
                className="w-full sm:text-sm text-xs"
              />
              {errors.name && <p className="mt-1 text-xs text-rose-500">{errors.name.message}</p>}
            </div>

            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Mã kho (Tùy chọn)
              </label>
              <Input
                {...register('code')}
                placeholder="WH-001"
                className="w-full sm:text-sm text-xs uppercase"
              />
            </div>

            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Vị trí / Địa chỉ
              </label>
              <Input
                {...register('location')}
                placeholder="Địa chỉ vật lý của kho"
                className="w-full sm:text-sm text-xs"
              />
            </div>

            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="is_active"
                {...register('is_active')}
                className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <label htmlFor="is_active" className="sm:text-sm text-xs text-slate-700">
                Đang hoạt động (Cho phép nhập/xuất vật tư)
              </label>
            </div>
          </form>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 sm:text-sm text-xs font-medium text-slate-700 hover:bg-slate-200 rounded-md transition-colors"
          >
            Hủy bỏ
          </button>
          <button
            type="submit"
            form="warehouse-form"
            disabled={isSaving}
            className="inline-flex items-center justify-center gap-2 px-6 py-2 sm:text-sm text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Đang lưu...' : 'Lưu lại'}
          </button>
        </div>
      </div>
    </div>
  );
}
